import { useEffect, useState } from "react";
import { connect } from "react-redux";

// Formik and Yup
import { useFormik } from "formik";
import Select from "react-select";
import * as Yup from "yup";

// Utils component
import TextError from "utils/TextError";

// Dashboard example components
import DataTable from "examples/Tables/DataTable";

// Dashboard components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDCard from "components/MDCard";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";

// @mui material components
import { Backdrop } from "@mui/material";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Modal from "@mui/material/Modal";
import Tooltip from "@mui/material/Tooltip";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import DownloadButton from "components/standardized-components/DownloadButton";
import DownloadDataButton from "components/standardized-components/DownloadDataButton";
import UploadButton from "components/standardized-components/UploadButton";
import { CSVLink } from "react-csv";
import { toast } from "react-toastify";

import CloseIcon from "@mui/icons-material/Close";
import Icon from "@mui/material/Icon";

import { CustomToastContent } from "../../../examples/CustomToastContent/index";

// Custom standard components
import CreateButton from "components/standardized-components/CreateButton";
import DeleteButton from "components/standardized-components/DeleteButton";
import EditButton from "components/standardized-components/EditButton";

import {
  createChemicalAnalysis,
  deleteChemicalAnalysis,
  editChemicalAnalysis,
  getChemicalAnalysis,
  getProductsListForDpp,
  getSubModulesByRoleId,
  uploadCsvForChemicalanalysis,
} from "../../../store";

// Cookies
import Cookies from "universal-cookie";

const cookies = new Cookies();

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: 350, lg: 850 },
  height: "fit-content",
  maxHeight: "90%",
  boxShadow: 24,
  p: 0,
  pt: 2,
  overflowY: "hidden",
  overflowX: "hidden",
};

const styleForEdit = { ...style, p: 1, pt: 3 };

const deleteStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 350,
  boxShadow: 24,
  p: 2,
  pt: 3,
};

const ChemicalAnalysis = ({
  getChemicalAnalysisReducer,
  getProductsListForDppReducer,
  getChemicalAnalysis,
  createChemicalAnalysis,
  editChemicalAnalysis,
  deleteChemicalAnalysis,
  getProductsListForDpp,
  dppIdSelectedForGlobalFilter,
  getSubModulesByRoleId,
  getSubModulesByRoleIdReducer,
  uploadCsvForChemicalanalysis,
}) => {
  let loginDetails = cookies.get("loginDetailsForWMS");
  let jwtToken = loginDetails?.jwt;
  let userId = loginDetails?.data?.userId || "system";
  let roleId = loginDetails.data.roleId;

  useEffect(() => {
    getSubModulesByRoleId(roleId, jwtToken);
  }, [roleId]);

  const [modulesData, setModulesData] = useState([]);

  useEffect(() => {
    setModulesData(
      getSubModulesByRoleIdReducer.subModulesByRoleData.data
        ? getSubModulesByRoleIdReducer.subModulesByRoleData.data
        : [],
    );
  }, [getSubModulesByRoleIdReducer]);

  let ItemLocationMapping = modulesData.find(
    (moduleId) => moduleId.moduleId == "80f14403-a4be-4883-9534-b4c9ed6aaeb8",
  );

  let allocateAccess = ItemLocationMapping
    ? ItemLocationMapping.actionId.includes(
      "b1dedc9d-b5d5-4aea-b48e-041fc78d979d",
    )
    : null;

  let viewAccess = ItemLocationMapping
    ? ItemLocationMapping.actionId.includes(
      "8df11607-c82a-4755-84c0-05602b85d9bf",
    )
    : null;
  let editAccess = ItemLocationMapping
    ? ItemLocationMapping.actionId.includes(
      "0b23bea3-dbdb-4d3a-b763-30b6a7c9129c",
    )
    : null;
  let deleteAccess = ItemLocationMapping
    ? ItemLocationMapping.actionId.includes(
      "22e8ce2b-2151-4742-a4e5-66ef075f3aee",
    )
    : null;

  const [loading, setLoading] = useState(true);

  // For DPP Dropdown Options
  const [dppOptions, setDPPOptions] = useState([]);

  // Fetch initial DPPs and set options
  useEffect(() => {
    getProductsListForDpp(jwtToken);
  }, [jwtToken]);

  useEffect(() => {
    let tempDppOptions = [];
    let dataList = getProductsListForDppReducer?.productsListForDpp?.data
      ? getProductsListForDppReducer.productsListForDpp.data
      : [];

    dataList.forEach((item) => {
      tempDppOptions.push({
        label: item.dppCode,
        value: item.dppId,
      });
    });
    setDPPOptions(tempDppOptions);
  }, [getProductsListForDppReducer]);

  // Fetch Table Data
  useEffect(() => {
    if (dppIdSelectedForGlobalFilter?.value) {
      getChemicalAnalysis(dppIdSelectedForGlobalFilter?.value, jwtToken);
    } else {
      getChemicalAnalysis(null, jwtToken);
    }
  }, [dppIdSelectedForGlobalFilter, jwtToken]);

  // DataTable State
  const [state, setState] = useState({
    columns: [
      { Header: "ID", accessor: "chemicalAnalysisId" },
      { Header: "DPP Code", accessor: "dppCode" },
      { Header: "Al", accessor: "massFraction_AI" },
      { Header: "Be", accessor: "massFraction_Be" },
      { Header: "C", accessor: "massFraction_C" },
      { Header: "Cr", accessor: "massFraction_Cr" },
      { Header: "Cu", accessor: "massFraction_Cu" },
      { Header: "Mn", accessor: "massFraction_Mn" },
      { Header: "Nb", accessor: "massFraction_Nb" },
      {
        Header: "Action",
        accessor: "action",
        cannotSearch: true,
        cannotSort: true,
      },
    ],
    rows: [],
  });

  const { columns, rows } = state;

  const headers = [
    { label: "DPP Code", key: "dppCode" },
    { label: "Aluminium Mass Fraction", key: "aluminiumMassFraction" },
    { label: "Beryllium Mass Fraction", key: "berylliumMassFraction" },
    { label: "Carbon Mass Fraction", key: "carbonMassFraction" },
    { label: "Chromium Mass Fraction", key: "chromiumMassFraction" },
    { label: "Copper Mass Fraction", key: "copperMassFraction" },
    { label: "Manganese Mass Fraction", key: "manganeseMassFraction" },
    { label: "Niobium Mass Fraction", key: "niobiumMassFraction" },
    { label: "Phosphorus Mass Fraction", key: "phosphorusMassFraction" },
    { label: "Lead Mass Fraction", key: "leadMassFraction" },
    { label: "Molybdenum Mass Fraction", key: "molybdenumMassFraction" },
  ];

  const headersForTemp = [
    { label: "dppCode", key: "dppCode" },
    { label: "aluminiumMassFraction", key: "aluminiumMassFraction" },
    { label: "berylliumMassFraction", key: "berylliumMassFraction" },
    { label: "carbonMassFraction", key: "carbonMassFraction" },
    { label: "chromiumMassFraction", key: "chromiumMassFraction" },
    { label: "copperMassFraction", key: "copperMassFraction" },
    { label: "manganeseMassFraction", key: "manganeseMassFraction" },
    { label: "niobiumMassFraction", key: "niobiumMassFraction" },
    { label: "phosphorusMassFraction", key: "phosphorusMassFraction" },
    { label: "leadMassFraction", key: "leadMassFraction" },
    { label: "molybdenumMassFraction", key: "molybdenumMassFraction" },
  ];

  const csvExportData = (
    getChemicalAnalysisReducer?.chemicalAnalysis?.data || []
  ).map((item) => ({
    dppCode: item.dppCode,
    aluminiumMassFraction: item.massFraction_AI,
    berylliumMassFraction: item.massFraction_Be,
    carbonMassFraction: item.massFraction_C,
    chromiumMassFraction: item.massFraction_Cr,
    copperMassFraction: item.massFraction_Cu,
    manganeseMassFraction: item.massFraction_Mn,
    niobiumMassFraction: item.massFraction_Nb,
    phosphorusMassFraction: item.massFraction_P,
    leadMassFraction: item.massFraction_Pb,
    molybdenumMassFraction: item.massFraction_Mo,
  }));

  const csvLink = {
    filename: "Chemical Analysis List.csv",
    headers: headers,
    data: csvExportData,
  };

  const csvLink2 = {
    filename: "Chemical Analysis Upload Template.csv",
    headers: headersForTemp,
    data: [],
  };

  const [showUploadPanel, setShowUploadPanel] = useState(false);
  const [uploadCsvData, setUploadCsvData] = useState(null);

  const handleUploadClick = () => {
    setShowUploadPanel(!showUploadPanel);
    setUploadCsvData(null);
  };

  const uploadCsvHandler = async () => {
    if (!uploadCsvData) {
      toast.error("Please select a CSV file first!");
      return;
    }

    let formData = new FormData();
    formData.append("file", uploadCsvData);

    try {
      let res = await uploadCsvForChemicalanalysis(formData, jwtToken);

      if (res?.status === true || res?.data?.status === true) {
        setShowUploadPanel(false);
        setUploadCsvData(null);

        let filterId = dppIdSelectedForGlobalFilter?.value || null;
        getChemicalAnalysis(filterId, jwtToken);
      }

      showUploadNotification(res);
    } catch (error) {
      showUploadNotification(error);
    }
  };

  const showUploadNotification = (res) => {
    const payload = res?.response?.data || res?.data?.data || res?.data || res || {};

    const isSuccess = payload.status === true;
    const msg = payload.msg;
    const description = payload.description;

    const counts = {
      success: payload.successCount,
      error: payload.errorCount,
      duplicate: payload.duplicateCount
    };

    const toastOptions = {
      position: "top-center",
      autoClose: 5000,
      closeOnClick: false,
      draggable: true,
    };

    if (isSuccess) {
      toast.success(
        <CustomToastContent msg={msg} description={description} counts={counts} isSuccess={true} />,
        toastOptions
      );
    } else {
      toast.error(
        <CustomToastContent msg={msg} description={description} counts={counts} isSuccess={false} />,
        toastOptions
      );
    }
  };

  useEffect(() => {
    let tempMappedActivity = [];
    let data = getChemicalAnalysisReducer?.chemicalAnalysis?.data
      ? getChemicalAnalysisReducer.chemicalAnalysis.data
      : [];

    setLoading(getChemicalAnalysisReducer?.loading || false);

    data.map((item) => {
      const itemObject = {
        chemicalAnalysisId: item.chemicalAnalysisId,
        dppId: item.dppId,
        dppCode: item.dppCode,
        massFraction_AI: item.massFraction_AI,
        massFraction_Be: item.massFraction_Be,
        massFraction_C: item.massFraction_C,
        massFraction_Cr: item.massFraction_Cr,
        massFraction_Cu: item.massFraction_Cu,
        massFraction_Mn: item.massFraction_Mn,
        massFraction_Nb: item.massFraction_Nb,
        action: (
          <>
            {deleteAccess && (
              <DeleteButton
                tooltip="Delete"
                variant="gradient"
                color="error"
                iconOnly
                type="button"
                style={{ marginRight: "5px" }}
                onClick={() => handleOpenDeleteModal(item)}
              >
                <Icon>delete</Icon>
              </DeleteButton>
            )}

            {editAccess && (
              <EditButton
                tooltip="Edit"
                variant="gradient"
                color="success"
                iconOnly
                type="button"
                onClick={() => handleOpenUpdateModal(item)}
                style={{ marginRight: "5px" }}
              >
                <Icon>edit_sharp</Icon>
              </EditButton>
            )}
          </>
        ),
      };
      tempMappedActivity.push(itemObject);
    });

    setState((prevState) => ({ ...prevState, rows: tempMappedActivity }));
  }, [getChemicalAnalysisReducer]);

  // ================= CREATE LOGIC =================
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [isSuccessCreate, setIsSuccessCreate] = useState(false);
  const [isErrorCreate, setIsErrorCreate] = useState(false);
  const [errorMsgCreate, setErrorMsgCreate] = useState("");
  const [createButtonName, setCreateButtonName] = useState("Create");

  const handleOpenCreateModal = () => {
    setOpenCreateModal(true);
    formik.setErrors({});
  };

  const handleCloseCreateModal = (event, reason) => {
    if (reason === "backdropClick") return;
    setOpenCreateModal(false);
    formik.resetForm();
  };

  useEffect(() => {
    if (isSuccessCreate) setTimeout(() => setIsSuccessCreate(false), 3000);
    if (isErrorCreate) setTimeout(() => setIsErrorCreate(false), 3000);
  }, [isSuccessCreate, isErrorCreate]);

  const initialValues = {
    DPPID: "",
    massFraction_AI: "",
    massFraction_Be: "",
    massFraction_C: "",
    massFraction_Cr: "",
    massFraction_Cu: "",
    massFraction_Mn: "",
    massFraction_Nb: "",
    massFraction_P: "",
    massFraction_Pb: "",
    massFraction_Mo: "",
  };

  const validationSchema = Yup.object({
    DPPID: Yup.object().required("Select DPP Code"),
    massFraction_AI: Yup.number()
      .typeError("Must be a number")
      .required("Required"),
    massFraction_Be: Yup.number()
      .typeError("Must be a number")
      .required("Required"),
    massFraction_C: Yup.number()
      .typeError("Must be a number")
      .required("Required"),
    massFraction_Cr: Yup.number()
      .typeError("Must be a number")
      .required("Required"),
    massFraction_Cu: Yup.number()
      .typeError("Must be a number")
      .required("Required"),
    massFraction_Mn: Yup.number()
      .typeError("Must be a number")
      .required("Required"),
    massFraction_Nb: Yup.number()
      .typeError("Must be a number")
      .required("Required"),
    massFraction_P: Yup.number()
      .typeError("Must be a number")
      .required("Required"),
    massFraction_Pb: Yup.number()
      .typeError("Must be a number")
      .required("Required"),
    massFraction_Mo: Yup.number()
      .typeError("Must be a number")
      .required("Required"),
  });

  const onSubmit = async (values, { resetForm }) => {
    setCreateButtonName("Please wait...");
    const payload = {
      dppId: values.DPPID?.value,
      massFraction_AI: String(values.massFraction_AI),
      massFraction_Be: String(values.massFraction_Be),
      massFraction_C: String(values.massFraction_C),
      massFraction_Cr: String(values.massFraction_Cr),
      massFraction_Cu: String(values.massFraction_Cu),
      massFraction_Mn: String(values.massFraction_Mn),
      massFraction_Nb: String(values.massFraction_Nb),
      massFraction_P: String(values.massFraction_P),
      massFraction_Pb: String(values.massFraction_Pb),
      massFraction_Mo: String(values.massFraction_Mo),
      cdBy: userId,
    };

    let res = await createChemicalAnalysis(payload, jwtToken);

    if (res.status) {
      setIsSuccessCreate(true);
      getChemicalAnalysis(dppIdSelectedForGlobalFilter?.value, jwtToken);
      setTimeout(() => {
        setCreateButtonName("Create");
        handleCloseCreateModal();
      }, 2000);
    } else {
      setErrorMsgCreate(res?.data?.msg || "Something went wrong");
      setIsErrorCreate(true);
      setCreateButtonName("Create");
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  // ================= EDIT LOGIC =================
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [singleDataForUpdate, setSingleDataForUpdate] = useState({});
  const [isSuccessForUpdate, setIsSuccessForUpdate] = useState(false);
  const [isErrorForUpdate, setIsErrorForUpdate] = useState(false);
  const [errorMsgForUpdate, setErrorMsgForUpdate] = useState("");
  const [editButtonName, setEditButtonName] = useState("Update");

  const handleOpenUpdateModal = (data) => {
    setSingleDataForUpdate(data);
    setOpenUpdateModal(true);
  };

  const handleCloseUpdateModal = (event, reason) => {
    if (reason === "backdropClick") return;
    setOpenUpdateModal(false);
    formikForUpdate.resetForm();
  };

  useEffect(() => {
    if (isSuccessForUpdate)
      setTimeout(() => setIsSuccessForUpdate(false), 3000);
    if (isErrorForUpdate) setTimeout(() => setIsErrorForUpdate(false), 3000);
  }, [isSuccessForUpdate, isErrorForUpdate]);

  const initialValueForUpdate = {
    chemicalAnalysisId: singleDataForUpdate.chemicalAnalysisId || "",
    DPPID: singleDataForUpdate.dppCode || "",
    massFraction_AI: singleDataForUpdate.massFraction_AI || "",
    massFraction_Be: singleDataForUpdate.massFraction_Be || "",
    massFraction_C: singleDataForUpdate.massFraction_C || "",
    massFraction_Cr: singleDataForUpdate.massFraction_Cr || "",
    massFraction_Cu: singleDataForUpdate.massFraction_Cu || "",
    massFraction_Mn: singleDataForUpdate.massFraction_Mn || "",
    massFraction_Nb: singleDataForUpdate.massFraction_Nb || "",
    massFraction_P: singleDataForUpdate.massFraction_P || "",
    massFraction_Pb: singleDataForUpdate.massFraction_Pb || "",
    massFraction_Mo: singleDataForUpdate.massFraction_Mo || "",
  };

  // Skip DPPID validation on edit since it's disabled
  const validationSchemaEdit = Yup.object({
    massFraction_AI: Yup.number()
      .typeError("Must be a number")
      .required("Required"),
    massFraction_Be: Yup.number()
      .typeError("Must be a number")
      .required("Required"),
    massFraction_C: Yup.number()
      .typeError("Must be a number")
      .required("Required"),
    massFraction_Cr: Yup.number()
      .typeError("Must be a number")
      .required("Required"),
    massFraction_Cu: Yup.number()
      .typeError("Must be a number")
      .required("Required"),
    massFraction_Mn: Yup.number()
      .typeError("Must be a number")
      .required("Required"),
    massFraction_Nb: Yup.number()
      .typeError("Must be a number")
      .required("Required"),
    massFraction_P: Yup.number()
      .typeError("Must be a number")
      .required("Required"),
    massFraction_Pb: Yup.number()
      .typeError("Must be a number")
      .required("Required"),
    massFraction_Mo: Yup.number()
      .typeError("Must be a number")
      .required("Required"),
  });

  const onSubmitForUpdate = async (values) => {
    setEditButtonName("Please wait...");
    const payload = {
      chemicalAnalysisId: values.chemicalAnalysisId,
      dppId: singleDataForUpdate.dppId, // keep original ID
      massFraction_AI: String(values.massFraction_AI),
      massFraction_Be: String(values.massFraction_Be),
      massFraction_C: String(values.massFraction_C),
      massFraction_Cr: String(values.massFraction_Cr),
      massFraction_Cu: String(values.massFraction_Cu),
      massFraction_Mn: String(values.massFraction_Mn),
      massFraction_Nb: String(values.massFraction_Nb),
      massFraction_P: String(values.massFraction_P),
      massFraction_Pb: String(values.massFraction_Pb),
      massFraction_Mo: String(values.massFraction_Mo),
      udBy: userId,
    };

    let res = await editChemicalAnalysis(payload, jwtToken);

    if (res.status) {
      setIsSuccessForUpdate(true);
      getChemicalAnalysis(dppIdSelectedForGlobalFilter?.value, jwtToken);
      setTimeout(() => {
        handleCloseUpdateModal();
        setEditButtonName("Update");
      }, 2000);
    } else {
      setIsErrorForUpdate(true);
      setErrorMsgForUpdate(res?.data?.msg || "Failed to update");
      setEditButtonName("Update");
    }
  };

  const formikForUpdate = useFormik({
    initialValues: initialValueForUpdate,
    validationSchema: validationSchemaEdit,
    onSubmit: onSubmitForUpdate,
    enableReinitialize: true,
  });

  // ================= DELETE LOGIC =================
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [singleDataForDelete, setSingleDataForDelete] = useState({});
  const [isSuccessForDelete, setIsSuccessForDelete] = useState(false);
  const [isErrorForDelete, setIsErrorForDelete] = useState(false);
  const [errorMsgForDelete, setErrorMsgForDelete] = useState("");
  const [deleteButtonName, setDeleteButtonName] = useState("Delete");

  const handleOpenDeleteModal = (data) => {
    setSingleDataForDelete(data);
    setOpenDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
  };

  useEffect(() => {
    if (isSuccessForDelete)
      setTimeout(() => setIsSuccessForDelete(false), 2000);
    if (isErrorForDelete) setTimeout(() => setIsErrorForDelete(false), 3000);
  }, [isSuccessForDelete, isErrorForDelete]);

  const deleteRecord = async () => {
    setDeleteButtonName("Please wait...");
    let id = singleDataForDelete.chemicalAnalysisId;
    let cd = userId;

    let res = await deleteChemicalAnalysis(id, cd, jwtToken);

    if (res.status) {
      setIsSuccessForDelete(true);
      getChemicalAnalysis(dppIdSelectedForGlobalFilter?.value, jwtToken);
      setTimeout(() => {
        handleCloseDeleteModal();
        setDeleteButtonName("Delete");
      }, 2000);
    } else {
      setIsErrorForDelete(true);
      setErrorMsgForDelete(res?.data?.msg || "Failed to delete");
      setDeleteButtonName("Delete");
    }
  };

  // Helper for generating fields easily to reduce boilerplate in JSX
  const massFields = ["AI", "Be", "C", "Cr", "Cu", "Mn", "Nb", "P", "Pb", "Mo"];

  return (
    <div>
      <MDBox pt={0} pb={0} mb={-2.5}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            {/* Header Actions */}
            {/* <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              p={0}
              mb={-4.5}
              mt={-1}
            >
              <MDBox color="text" px={2} ml={-2} mb={4}>
                {allocateAccess && (
                  <Tooltip title="Create Chemical Analysis">
                    <CreateButton
                      color="info"
                      variant="contained"
                      type="button"
                      iconOnly
                      onClick={handleOpenCreateModal}
                      style={{ borderRadius: "5px" }}
                      size="small"
                    >
                      <Icon>add</Icon>
                    </CreateButton>
                  </Tooltip>
                )}
              </MDBox>
            </MDBox> */}

            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              p={0}
              mb={-4.5}
              mt={-1}
            >
              <MDBox
                color="text"
                px={2}
                ml={-2}
                mb={4}
                display="flex"
                alignItems="center"
              >
                {allocateAccess && (
                  <MDBox display="flex" alignItems="center">
                    <Tooltip>
                      <CreateButton
                        tooltip="Add Chemical Analysis"
                        color="info"
                        variant="contained"
                        type="button"
                        iconOnly
                        onClick={handleOpenCreateModal}
                        style={{ borderRadius: "5px" }}
                        size="small"
                      >
                        <Icon>add</Icon>
                      </CreateButton>
                    </Tooltip>

                    <CSVLink {...csvLink}>
                      <DownloadDataButton
                        tooltip="Download Chemical Analysis List"
                        style={{ marginLeft: "1rem" }}
                        type="button"
                        variant="outlined"
                        color="error"
                        iconOnly
                      >
                        <Icon>download</Icon>
                      </DownloadDataButton>
                    </CSVLink>

                    <UploadButton
                      tooltip="Upload CSV"
                      style={{ marginLeft: "1rem", marginTop: "2px" }}
                      type="button"
                      variant={showUploadPanel ? "contained" : "outlined"}
                      color="info"
                      iconOnly
                      onClick={handleUploadClick}
                    >
                      <Icon>upload</Icon>
                    </UploadButton>

                    <CSVLink {...csvLink2}>
                      <DownloadButton
                        tooltip="Download Template"
                        style={{ marginLeft: "1rem" }}
                        type="button"
                        variant="outlined"
                        color="error"
                        iconOnly
                      >
                        <Icon>description</Icon>
                      </DownloadButton>
                    </CSVLink>

                    {showUploadPanel && (
                      <MDBox
                        display="flex"
                        alignItems="center"
                        ml={2}
                        p={0.5}
                        px={2}
                      // sx={{ border: "1px dashed #ccc", borderRadius: "8px" }}
                      >
                        <input
                          className="choose_file"
                          type="file"
                          accept=".csv"
                          onChange={(e) => setUploadCsvData(e.target.files[0])}
                          style={{ width: "200px" }}
                        />

                        <MDButton
                          disabled={
                            !uploadCsvData?.name ||
                            uploadCsvData?.type !== "text/csv"
                          }
                          variant="contained"
                          color="info"
                          style={{ marginLeft: "1rem" }}
                          onClick={uploadCsvHandler}
                        >
                          UPLOAD
                        </MDButton>

                        <IconButton
                          size="small"
                          onClick={() => setShowUploadPanel(false)}
                          style={{ marginLeft: "10px", color: "gray" }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </MDBox>
                    )}
                  </MDBox>
                )}
              </MDBox>
            </MDBox>

            {/* DataTable */}
            <MDBox pt={0}>
              <DataTable
                table={{ columns, rows }}
                isSorted={true}
                tableSearch={true}
                entriesPerPage={{
                  defaultValue: 5,
                  entries: [5, 10, 15, 20, 25],
                }}
                showTotalEntries={true}
                noEndBorder
              />
            </MDBox>

            {/* CREATE MODAL */}
            <Modal open={openCreateModal} onClose={handleCloseCreateModal}>
              <MDCard sx={style}>
                <MDBox ml={3} mt={-1} display="flex" alignItems="center">
                  <MDTypography variant="h5" fontWeight="medium" flexGrow={1}>
                    Create Chemical Analysis
                  </MDTypography>
                  <Tooltip title="Close">
                    <IconButton
                      style={{
                        background: "whitesmoke",
                        color: "black",
                        borderRadius: 5,
                        marginRight: "10px",
                        marginTop: "4px",
                      }}
                      onClick={handleCloseCreateModal}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Tooltip>
                </MDBox>
                <hr style={{ marginTop: "0.5rem", marginBottom: "0rem" }} />

                <MDBox pt={1} pb={3} px={3}>
                  <MDBox
                    component="form"
                    role="form"
                    onSubmit={formik.handleSubmit}
                  >
                    <Grid container spacing={2}>
                      {/* DPP Dropdown */}
                      <Grid item xs={12} lg={4}>
                        <MDBox mb={1} ml={1}>
                          <MDTypography
                            variant="h6"
                            style={{ fontWeight: "500" }}
                          >
                            DPP Code
                          </MDTypography>
                          <Select
                            isClearable
                            className="select-css"
                            placeholder="Choose one..."
                            name="DPPID"
                            value={formik.values.DPPID}
                            options={dppOptions}
                            onBlur={() =>
                              formik.handleBlur({ target: { name: "DPPID" } })
                            }
                            onChange={(selectedOption) => {
                              formik.setFieldValue("DPPID", selectedOption);
                            }}
                            menuPortalTarget={document.body}
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                          />
                          {formik.touched.DPPID && formik.errors.DPPID && (
                            <TextError msg={formik.errors.DPPID} />
                          )}
                        </MDBox>
                      </Grid>

                      {/* Map through all mass fraction fields */}
                      {massFields.map((field, index) => (
                        <Grid item xs={12} lg={4} key={index}>
                          <MDBox mb={1} ml={1}>
                            <MDTypography variant="h6">
                              Mass Fraction {field}
                            </MDTypography>
                            <MDInput
                              size="small"
                              type="number"
                              name={`massFraction_${field}`}
                              fullWidth
                              autoComplete="off"
                              value={formik.values[`massFraction_${field}`]}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              error={
                                formik.touched[`massFraction_${field}`] &&
                                Boolean(formik.errors[`massFraction_${field}`])
                              }
                            />
                            {formik.touched[`massFraction_${field}`] &&
                              formik.errors[`massFraction_${field}`] && (
                                <TextError
                                  msg={formik.errors[`massFraction_${field}`]}
                                />
                              )}
                          </MDBox>
                        </Grid>
                      ))}

                      <Grid item xs={12}>
                        <Collapse in={isErrorCreate}>
                          <Alert
                            severity="error"
                            action={
                              <IconButton
                                size="small"
                                onClick={() => setIsErrorCreate(false)}
                              >
                                <CloseIcon fontSize="inherit" />
                              </IconButton>
                            }
                          >
                            {errorMsgCreate}
                          </Alert>
                        </Collapse>
                        <Collapse in={isSuccessCreate}>
                          <Alert
                            severity="success"
                            action={
                              <IconButton
                                size="small"
                                onClick={() => setIsSuccessCreate(false)}
                              >
                                <CloseIcon fontSize="inherit" />
                              </IconButton>
                            }
                          >
                            Created Successfully
                          </Alert>
                        </Collapse>
                        <MDBox mt={2}>
                          <MDButton
                            color="info"
                            type="submit"
                            fullWidth
                            disabled={createButtonName === "Please wait..."}
                          >
                            {createButtonName}
                          </MDButton>
                        </MDBox>
                      </Grid>
                    </Grid>
                  </MDBox>
                </MDBox>
              </MDCard>
            </Modal>

            {/* UPDATE MODAL */}
            <Modal open={openUpdateModal} onClose={handleCloseUpdateModal}>
              <MDCard sx={styleForEdit}>
                <MDBox ml={3} mt={-1} display="flex" alignItems="center">
                  <MDTypography variant="h4" fontWeight="medium" flexGrow={1}>
                    Update Chemical Analysis
                  </MDTypography>
                  <Tooltip title="Close">
                    <IconButton
                      style={{
                        background: "whitesmoke",
                        color: "black",
                        borderRadius: 5,
                        marginRight: "10px",
                        marginTop: "4px",
                      }}
                      onClick={handleCloseUpdateModal}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Tooltip>
                </MDBox>
                <hr style={{ marginTop: "0.5rem", marginBottom: "0rem" }} />

                <MDBox pt={1} pb={3} px={3}>
                  <MDBox
                    component="form"
                    role="form"
                    onSubmit={formikForUpdate.handleSubmit}
                  >
                    <Grid container spacing={2}>
                      {/* Readonly DPP Code */}
                      <Grid item xs={12} lg={4}>
                        <MDBox mb={1} ml={1}>
                          <MDTypography variant="h6">DPP Code</MDTypography>
                          <MDInput
                            size="small"
                            type="text"
                            name="DPPID"
                            fullWidth
                            disabled={true}
                            value={formikForUpdate.values.DPPID}
                          />
                        </MDBox>
                      </Grid>

                      {/* Map through all mass fraction fields for edit */}
                      {massFields.map((field, index) => (
                        <Grid item xs={12} lg={4} key={index}>
                          <MDBox mb={1} ml={1}>
                            <MDTypography variant="h6">
                              Mass Fraction {field}
                            </MDTypography>
                            <MDInput
                              size="small"
                              type="number"
                              name={`massFraction_${field}`}
                              fullWidth
                              autoComplete="off"
                              value={
                                formikForUpdate.values[`massFraction_${field}`]
                              }
                              onChange={formikForUpdate.handleChange}
                              onBlur={formikForUpdate.handleBlur}
                              error={
                                formikForUpdate.touched[
                                `massFraction_${field}`
                                ] &&
                                Boolean(
                                  formikForUpdate.errors[
                                  `massFraction_${field}`
                                  ],
                                )
                              }
                            />
                            {formikForUpdate.touched[`massFraction_${field}`] &&
                              formikForUpdate.errors[
                              `massFraction_${field}`
                              ] && (
                                <TextError
                                  msg={
                                    formikForUpdate.errors[
                                    `massFraction_${field}`
                                    ]
                                  }
                                />
                              )}
                          </MDBox>
                        </Grid>
                      ))}

                      <Grid item xs={12}>
                        <Collapse in={isErrorForUpdate}>
                          <Alert
                            severity="error"
                            action={
                              <IconButton
                                size="small"
                                onClick={() => setIsErrorForUpdate(false)}
                              >
                                <CloseIcon fontSize="inherit" />
                              </IconButton>
                            }
                          >
                            {errorMsgForUpdate}
                          </Alert>
                        </Collapse>
                        <Collapse in={isSuccessForUpdate}>
                          <Alert
                            severity="success"
                            action={
                              <IconButton
                                size="small"
                                onClick={() => setIsSuccessForUpdate(false)}
                              >
                                <CloseIcon fontSize="inherit" />
                              </IconButton>
                            }
                          >
                            Updated successfully
                          </Alert>
                        </Collapse>
                        <MDBox mt={2}>
                          <MDButton
                            color="info"
                            type="submit"
                            fullWidth
                            disabled={editButtonName === "Please wait..."}
                          >
                            {editButtonName}
                          </MDButton>
                        </MDBox>
                      </Grid>
                    </Grid>
                  </MDBox>
                </MDBox>
              </MDCard>
            </Modal>

            {/* DELETE MODAL */}
            <Modal open={openDeleteModal} onClose={handleCloseDeleteModal}>
              <MDCard sx={deleteStyle}>
                <MDTypography>Are you sure you want to delete?</MDTypography>
                <MDBox mt={2} display="flex" justifyContent="space-between">
                  <MDButton
                    color="info"
                    variant="outlined"
                    onClick={handleCloseDeleteModal}
                  >
                    Cancel
                  </MDButton>
                  <MDButton
                    color="error"
                    onClick={deleteRecord}
                    disabled={deleteButtonName === "Please wait..."}
                  >
                    {deleteButtonName}
                  </MDButton>
                </MDBox>
                <MDBox mt={2}>
                  <Collapse in={isErrorForDelete}>
                    <Alert
                      severity="error"
                      action={
                        <IconButton
                          size="small"
                          onClick={() => setIsErrorForDelete(false)}
                        >
                          <CloseIcon fontSize="inherit" />
                        </IconButton>
                      }
                    >
                      {errorMsgForDelete}
                    </Alert>
                  </Collapse>
                  <Collapse in={isSuccessForDelete}>
                    <Alert
                      severity="success"
                      action={
                        <IconButton
                          size="small"
                          onClick={() => setIsSuccessForDelete(false)}
                        >
                          <CloseIcon fontSize="inherit" />
                        </IconButton>
                      }
                    >
                      Deleted Successfully
                    </Alert>
                  </Collapse>
                </MDBox>
              </MDCard>
            </Modal>
          </Grid>
        </Grid>
      </MDBox>

      {loading && (
        <Backdrop
          sx={{ color: "blue", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <ToastContainer />
    </div>
  );
};

const mapStateToProps = (state) => ({
  getChemicalAnalysisReducer: state.getChemicalAnalysisReducer,
  getProductsListForDppReducer: state.getProductsListForDppReducer,
  getSubModulesByRoleIdReducer: state.getSubModulesByRoleIdReducer,
});

const mapDispatchToProps = (dispatch) => ({
  getSubModulesByRoleId: (roleId, jwtToken) =>
    dispatch(getSubModulesByRoleId(roleId, jwtToken)),
  getChemicalAnalysis: (id, jwtToken) =>
    dispatch(getChemicalAnalysis(id, jwtToken)),
  createChemicalAnalysis: (payload, jwtToken) =>
    dispatch(createChemicalAnalysis(payload, jwtToken)),
  editChemicalAnalysis: (payload, jwtToken) =>
    dispatch(editChemicalAnalysis(payload, jwtToken)),
  deleteChemicalAnalysis: (id, cd, jwtToken) =>
    dispatch(deleteChemicalAnalysis(id, cd, jwtToken)),
  getProductsListForDpp: (jwtToken) =>
    dispatch(getProductsListForDpp(jwtToken)),
  uploadCsvForChemicalanalysis: (payload, jwtToken) =>
    dispatch(uploadCsvForChemicalanalysis(payload, jwtToken)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ChemicalAnalysis);
